import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesExecutionForm,
  selectGuaranteesExecutionFormError,
  selectGuaranteesExecutionFormStatus,
  selectGuaranteesPatchExecutionFormError,
  selectGuaranteesPatchExecutionFormStatus,
  selectGuaranteesSubmitApplicationByUakdoError,
  selectGuaranteesSubmitApplicationByUakdoStatus,
  selectGuaranteesUakdoSupervisorInfoStatus,
  selectHasGuaranteesUakdoResponsibleAccess,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesApplication,
  fetchGuaranteesUakdoSupervisorInfo,
  patchGuaranteesExecutionForm,
  submitGuaranteesApplicationByUakdo,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  type TPatchGuaranteesExecutionFormPayload,
} from '~/store/guarantees/types.ts';
import {
  getGuaranteesPayloadVersion,
  normalizeGuaranteesApplicationStatus,
} from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import { UAKDO_CONTROL_TEXT } from './constants.ts';
import UakdoControlSection from './UakdoControlSection.tsx';
import { getGuaranteesUakdoSecurityType } from './utils.ts';

const uakdoControlEditableStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_UAKDO_APPROVAL,
  GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_APPROVAL,
]);

const UakdoControlPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const executionFormFromStore = useSelector(selectGuaranteesExecutionForm);
  const executionFormStatus = useSelector(selectGuaranteesExecutionFormStatus);
  const executionFormError = useSelector(selectGuaranteesExecutionFormError);
  const patchExecutionFormStatus = useSelector(selectGuaranteesPatchExecutionFormStatus);
  const patchExecutionFormError = useSelector(selectGuaranteesPatchExecutionFormError);
  const submitByUakdoStatus = useSelector(selectGuaranteesSubmitApplicationByUakdoStatus);
  const submitByUakdoError = useSelector(selectGuaranteesSubmitApplicationByUakdoError);
  const uakdoSupervisorInfoStatus = useSelector(selectGuaranteesUakdoSupervisorInfoStatus);
  const hasUakdoResponsibleAccess = useSelector(selectHasGuaranteesUakdoResponsibleAccess);
  const redirectPath = !contragentId ? '/guarantees/administrator/get-started' : '';
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const executionForm = isLoadedApplicationCurrent
    ? executionFormFromStore ?? currentRouteApplication?.executionForm ?? null
    : null;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number' ? currentRouteApplication.version : 0;
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const securityType = getGuaranteesUakdoSecurityType(currentRouteApplication);
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isExecutionFormLoading = executionFormStatus === GuaranteesFetchStatus.Loading;
  const isSavingExecutionForm = patchExecutionFormStatus === GuaranteesFetchStatus.Loading;
  const isSubmittingByUakdo = submitByUakdoStatus === GuaranteesFetchStatus.Loading;
  const isUakdoAccessLoading =
    uakdoSupervisorInfoStatus === GuaranteesFetchStatus.Idle ||
    uakdoSupervisorInfoStatus === GuaranteesFetchStatus.Loading;
  const isUakdoAccessDenied = !isUakdoAccessLoading && !hasUakdoResponsibleAccess;
  const isUakdoControlEditable = uakdoControlEditableStatuses.has(normalizedStatus);
  const requestError =
    applicationError || executionFormError || patchExecutionFormError || submitByUakdoError;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate('/tasks'),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const navigateToTasks = useCallback(() => {
    navigate('/tasks', { replace: true });
  }, [navigate]);
  const handleCloseUakdoAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigateToTasks();
  }, [handleCloseAlertModal, navigateToTasks]);

  useEffect(() => {
    if (uakdoSupervisorInfoStatus === GuaranteesFetchStatus.Idle) {
      void dispatch(fetchGuaranteesUakdoSupervisorInfo());
    }
  }, [dispatch, uakdoSupervisorInfoStatus]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (isUakdoAccessDenied) {
      setModalAlert({ text: UAKDO_CONTROL_TEXT.accessDeniedText });
    }
  }, [isUakdoAccessDenied, setModalAlert]);

  useFreshGuaranteesRequestError(requestError, showGuaranteesRequestError);

  const handleSaveExecutionForm = useCallback(
    async (payload: TPatchGuaranteesExecutionFormPayload) => {
      if (!applicationId || !currentApplicationVersion) {
        return null;
      }

      try {
        const patchedExecutionForm = await dispatch(
          patchGuaranteesExecutionForm({
            applicationId,
            version: currentApplicationVersion,
            payload,
          })
        ).unwrap();

        setModalSuccess({
          text: UAKDO_CONTROL_TEXT.savedMessage,
          buttonValue: UAKDO_CONTROL_TEXT.successCloseButton,
        });

        return patchedExecutionForm;
      } catch (error) {
        showGuaranteesRequestError(error);
        throw error;
      }
    },
    [
      applicationId,
      currentApplicationVersion,
      dispatch,
      setModalSuccess,
      showGuaranteesRequestError,
    ]
  );

  const handleConfirmExecutionForm = useCallback(
    async (payload: TPatchGuaranteesExecutionFormPayload) => {
      if (!applicationId || !effectiveApplicationId || !currentApplicationVersion) {
        return;
      }

      try {
        const patchedExecutionForm = await dispatch(
          patchGuaranteesExecutionForm({
            applicationId,
            version: currentApplicationVersion,
            payload,
          })
        ).unwrap();
        const submitVersion =
          getGuaranteesPayloadVersion(patchedExecutionForm) ?? currentApplicationVersion;

        await dispatch(
          submitGuaranteesApplicationByUakdo({
            applicationId: effectiveApplicationId,
            version: submitVersion,
          })
        ).unwrap();
        setModalSuccess({
          text: UAKDO_CONTROL_TEXT.submittedMessage,
          buttonValue: UAKDO_CONTROL_TEXT.successCloseButton,
          onClick: navigateToTasks,
          onClose: navigateToTasks,
        });
      } catch (error) {
        showGuaranteesRequestError(error);
        throw error;
      }
    },
    [
      applicationId,
      currentApplicationVersion,
      dispatch,
      effectiveApplicationId,
      navigateToTasks,
      setModalSuccess,
      showGuaranteesRequestError,
    ]
  );

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isUakdoAccessLoading) {
    return <FullPageLoader />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={isUakdoAccessDenied ? handleCloseUakdoAccessModal : handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      <ApplicationHeader {...headerProps} />

      {isUakdoAccessDenied ? null : isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <UakdoControlSection
              executionForm={executionForm}
              isDisabled={!currentApplicationVersion || !isUakdoControlEditable}
              isConfirming={isSubmittingByUakdo}
              isLoading={isExecutionFormLoading}
              isSaving={isSavingExecutionForm}
              securityType={securityType}
              onConfirm={handleConfirmExecutionForm}
              onSubmit={handleSaveExecutionForm}
            />
          </GuaranteesManagerApplicationSidePanelLayout>
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default UakdoControlPage;
